import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  ClickAwayListener,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { useImageKeywordSuggestions } from "../../../hooks/useImageKeywordSuggestions";
import { StyledTextField, Wrapper, Title } from "./styled";
import { ImageKeywordSuggestionItemDto } from "@swagger/typescript-fetch-goflyer";

interface ImageKeywordAutocompleteProps {
  /**
   * Optional title displayed above the input field
   */
  title?: string;
  /**
   * Placeholder text for the input field
   */
  placeholder?: string;
  /**
   * Initial value for the input field
   */
  initialValue?: string;
  /**
   * Whether the input should be auto-focused
   */
  autoFocus?: boolean;
  /**
   * Whether to clear the search text (external control)
   */
  clearSearchText?: boolean;
  /**
   * Custom data test ID for testing
   */
  dataTestId?: string;
  dataTestIdInput?: string;
  dataTestIdSearchButton?: string;

  /**
   * Custom CSS styles for the wrapper
   */
  wrapperCss?: string;
  /**
   * Callback function when a search is triggered (Enter key or search button click)
   */
  onSearch?: (value: string) => void;
  /**
   * Callback function when a suggestion is selected
   */
  onSuggestionSelect?: (suggestion: string) => void;
  /**
   * Callback function when input value changes
   */
  onInputChange?: (value: string) => void;
  /**
   * Maximum number of suggestions to show
   * @default 8
   */
  maxSuggestions?: number;
  /**
   * Whether to show the search button
   * @default true
   */
  showSearchButton?: boolean;
  /**
   * Whether to show suggestions dropdown
   * @default true
   */
  showSuggestions?: boolean;
  /**
   * Custom styling options
   */
  styleVariant?: "default" | "modal" | "compact";
  /**
   * Delay in milliseconds before making API calls after user stops typing
   * @default 300
   */
  debounceDelay?: number;
  /**
   * z-index for suggestions dropdown popper
   * @default 1300
   */
  popperZIndex?: number;
  /**
   * Minimum length of query before making API calls
   * @default 2
   */
  minQueryLength?: number;
}

/**
 * ImageKeywordAutocomplete component provides real-time keyword suggestions for image search.
 * It integrates with the backend API to fetch relevant keywords as the user types.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ImageKeywordAutocomplete
 *   placeholder="Search for images..."
 *   onSearch={(value) => console.log('Searching for:', value)}
 *   onSuggestionSelect={(suggestion) => console.log('Selected:', suggestion)}
 * />
 *
 * // With custom styling for modal usage
 * <ImageKeywordAutocomplete
 *   styleVariant="modal"
 *   showSearchButton={false}
 *   onSuggestionSelect={(suggestion) => handleSuggestionSelect(suggestion)}
 * />
 * ```
 */
export const ImageKeywordAutocomplete: React.FC<
  ImageKeywordAutocompleteProps
> = ({
  title,
  placeholder,
  initialValue = "",
  autoFocus = false,
  clearSearchText = false,
  dataTestId = "image-keyword-autocomplete",
  wrapperCss,
  onSearch,
  onSuggestionSelect,
  onInputChange,
  maxSuggestions = 8,
  showSearchButton = true,
  showSuggestions = true,
  styleVariant = "default",
  debounceDelay = 300,
  popperZIndex = 1300,
  minQueryLength = 2,
  dataTestIdInput,
  dataTestIdSearchButton,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [userDismissedDropdown, setUserDismissedDropdown] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const { suggestions, isLoading, error, fetchSuggestions, clearSuggestions } =
    useImageKeywordSuggestions({
      debounceDelay,
      limit: maxSuggestions,
      minQueryLength,
    });

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchText(value);
      setHighlightedIndex(-1);
      setUserDismissedDropdown(false);

      onInputChange?.(value);

      if (showSuggestions && !userDismissedDropdown) {
        if (value.trim().length >= minQueryLength) {
          fetchSuggestions(value);
          setShowDropdown(true);
        } else {
          clearSuggestions();
          setShowDropdown(false);
        }
      }
    },
    [
      onInputChange,
      showSuggestions,
      fetchSuggestions,
      clearSuggestions,
      minQueryLength,
      userDismissedDropdown,
    ],
  );

  /**
   * Handle search execution
   */
  const handleSearch = useCallback(() => {
    onSearch?.(searchText.trim());
    setShowDropdown(false);
    setUserDismissedDropdown(true); // Mark as dismissed by user action
    inputRef.current?.blur();
  }, [searchText, onSearch]);

  /**
   * Handle suggestion selection
   */
  const handleSuggestionSelect = useCallback(
    (item: ImageKeywordSuggestionItemDto) => {
      const value = item.text;
      setSearchText(value);
      setShowDropdown(false);
      setUserDismissedDropdown(true);
      onSuggestionSelect?.(value);

      // If no onSuggestionSelect handler, default to search
      if (!onSuggestionSelect) {
        onSearch?.(value);
      }

      inputRef.current?.blur();
    },
    [onSuggestionSelect, onSearch],
  );

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!showDropdown || suggestions.length === 0) {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          handleSearch();
        }
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1,
          );
          break;
        case "Enter":
          event.preventDefault();
          event.stopPropagation();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[highlightedIndex]);
          } else {
            handleSearch();
          }
          break;
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          setShowDropdown(false);
          setHighlightedIndex(-1);
          setUserDismissedDropdown(true); // Mark as dismissed by user action
          inputRef.current?.blur();
          break;
      }
    },
    [
      showDropdown,
      suggestions,
      highlightedIndex,
      handleSearch,
      handleSuggestionSelect,
    ],
  );

  /**
   * Handle input focus
   */
  const handleInputFocus = useCallback(() => {
    // Only show dropdown on focus if user hasn't dismissed it and there are suggestions
    if (
      showSuggestions &&
      !userDismissedDropdown &&
      searchText.trim().length >= minQueryLength &&
      suggestions.length > 0
    ) {
      setShowDropdown(true);
    }
  }, [
    showSuggestions,
    userDismissedDropdown,
    searchText,
    minQueryLength,
    suggestions.length,
  ]);

  /**
   * Handle click away
   */
  const handleClickAway = useCallback(() => {
    setShowDropdown(false);
    setHighlightedIndex(-1);
    setUserDismissedDropdown(true);
  }, []);

  useEffect(() => {
    if (clearSearchText) {
      setSearchText("");
      setShowDropdown(false);
      setUserDismissedDropdown(false);
      clearSuggestions();
    }
  }, [clearSearchText, clearSuggestions]);

  useEffect(() => {
    setSearchText(initialValue);
    setUserDismissedDropdown(false);
  }, [initialValue]);

  /**
   * Get style variant specific configurations
   */
  const getStyleConfig = () => {
    switch (styleVariant) {
      case "modal":
        return {
          textFieldProps: {
            size: "medium" as const,
            sx: {
              "& .MuiInputBase-root": {
                borderRadius: 1,
                backgroundColor: "white",
              },
            },
          },
          paperProps: {
            sx: {
              maxHeight: 300,
              borderRadius: 1,
              boxShadow: 3,
            },
          },
        };
      case "compact":
        return {
          textFieldProps: {
            size: "small" as const,
            sx: {
              "& .MuiInputBase-root": {
                borderRadius: 2,
              },
            },
          },
          paperProps: {
            sx: {
              maxHeight: 240,
              borderRadius: 1,
            },
          },
        };
      default:
        return {
          textFieldProps: {
            size: "medium" as const,
          },
          paperProps: {
            sx: {
              maxHeight: 320,
            },
          },
        };
    }
  };

  const styleConfig = getStyleConfig();
  const shouldShowDropdown =
    showDropdown && showSuggestions && (suggestions.length > 0 || isLoading);

  return (
    <Wrapper wrapperCss={wrapperCss} data-testid={dataTestId} ref={anchorRef}>
      {title && <Title>{title}</Title>}

      <ClickAwayListener onClickAway={handleClickAway}>
        <Box position="relative" width="100%">
          <StyledTextField
            {...styleConfig.textFieldProps}
            inputRef={inputRef}
            autoFocus={autoFocus}
            placeholder={placeholder || (t(messages.Search()) as string)}
            fullWidth
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            data-testid={dataTestIdInput}
            InputProps={{
              endAdornment: showSearchButton ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                    data-testid={dataTestIdSearchButton}
                  >
                    <SearchIcon
                      style={{
                        color: "#fd954f",
                        fontSize: styleVariant === "compact" ? 20 : 24,
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          {shouldShowDropdown && (
            <Popper
              open={shouldShowDropdown}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              style={{
                width: anchorRef.current?.offsetWidth,
                zIndex: popperZIndex,
              }}
              modifiers={[
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                  },
                },
              ]}
            >
              <Paper {...styleConfig.paperProps}>
                {isLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={2}
                  >
                    <CircularProgress size={20} sx={{ color: "#ff8038" }} />
                  </Box>
                ) : error ? (
                  <Box py={2} px={2}>
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {suggestions.map((suggestion, index) => (
                      <ListItem
                        key={`${suggestion.text}-${index}`}
                        disablePadding
                        data-testid={`${dataTestId}-suggestion-${index}`}
                      >
                        <ListItemButton
                          selected={index === highlightedIndex}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#ff813896",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#ff813896",
                            },
                            "&:hover": {
                              backgroundColor: "#ff813896",
                            },
                          }}
                        >
                          {suggestion.src ? (
                            <Box
                              component="img"
                              src={suggestion.src}
                              alt={suggestion.text}
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 0.5,
                                objectFit: "cover",
                                mr: 1,
                                flexShrink: 0,
                              }}
                            />
                          ) : null}
                          <ListItemText
                            primary={suggestion.text}
                            primaryTypographyProps={{
                              variant: "body2",
                              noWrap: true,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Popper>
          )}
        </Box>
      </ClickAwayListener>
    </Wrapper>
  );
};
